import { createContext, useContext, useCallback, useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Bell, Clock, AlertCircle } from 'lucide-react';

type ToastType = 'reminder' | 'timeline' | 'error';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  createdAt: number;
}

interface ToastContextValue {
  addToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

const MAX_TOASTS = 5;
const AUTO_DISMISS_MS = 5000;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const addToast = useCallback((message: string, type: ToastType = 'reminder') => {
    const id = crypto.randomUUID();
    setToasts((prev) => {
      const next = [...prev, { id, message, type, createdAt: Date.now() }];
      return next.length > MAX_TOASTS ? next.slice(-MAX_TOASTS) : next;
    });
    const timer = setTimeout(() => dismissToast(id), AUTO_DISMISS_MS);
    timers.current.set(id, timer);
  }, [dismissToast]);

  useEffect(() => {
    return () => {
      timers.current.forEach((t) => clearTimeout(t));
    };
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col-reverse gap-2 pointer-events-none">
        <AnimatePresence mode="popLayout">
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              layout
              initial={{ opacity: 0, x: 80, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 80, scale: 0.95, transition: { duration: 0.15 } }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="pointer-events-auto w-72 surface-raised rounded-lg shadow-lg border border-border-subtle overflow-hidden"
            >
              <div className="flex items-start gap-2.5 px-3 py-2.5">
                <div className="shrink-0 mt-0.5">
                  {toast.type === 'reminder' ? (
                    <Bell size={14} className="text-accent" />
                  ) : toast.type === 'error' ? (
                    <AlertCircle size={14} className="text-red-400" />
                  ) : (
                    <Clock size={14} className="text-p2" />
                  )}
                </div>
                <p className="text-caption text-text-primary flex-1 min-w-0 break-words">
                  {toast.message}
                </p>
                <button
                  onClick={() => dismissToast(toast.id)}
                  className="shrink-0 text-text-muted hover:text-text-secondary p-0.5 rounded transition-colors"
                >
                  <X size={12} />
                </button>
              </div>
              <motion.div
                className="h-0.5 bg-accent/30 origin-left"
                initial={{ scaleX: 1 }}
                animate={{ scaleX: 0 }}
                transition={{ duration: AUTO_DISMISS_MS / 1000, ease: 'linear' }}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}
